import { RiseOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { getPropagationProbabilityColor } from 'utils/color/helpers';

import { FuturePropagationTag, TooltipLine } from './PredictedPropagation.style';
import {
  type PredictedPropagationSummary,
  buildGrowthTooltipSentence,
  getConfidenceLabel,
  getPillForecast,
} from './utils';

type Props = {
  summary: PredictedPropagationSummary | null;
};

export const PredictedPropagationTag = ({ summary }: Props) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  if (!summary) {
    return null;
  }

  const pill = getPillForecast(summary);
  if (!pill) {
    return null;
  }

  const sentence = buildGrowthTooltipSentence(summary, t, i18n.language);
  const confidence = getConfidenceLabel(summary.confidence, t);

  return (
    <Tooltip
      title={
        <>
          {sentence && <TooltipLine>{sentence}</TooltipLine>}
          {confidence && <TooltipLine>{confidence}</TooltipLine>}
        </>
      }
    >
      <FuturePropagationTag
        icon={<RiseOutlined />}
        $color={getPropagationProbabilityColor(theme, pill.percent)}
        data-testid="future-propagation-tag"
      >
        {pill.isFallback
          ? t(
              'damage.predictedPropagation.pillWithForecast',
              '{{value}}% Future Propagation ({{count}} mo)',
              { value: pill.percent, count: pill.months }
            )
          : t('damage.predictedPropagation.pill', '{{value}}% Future Propagation', {
              value: pill.percent,
            })}
      </FuturePropagationTag>
    </Tooltip>
  );
};
